import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHelmetProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
  structuredData?: object;
}

export const SEOHelmet: React.FC<SEOHelmetProps> = ({
  title,
  description,
  keywords = 'international banking, multicurrency accounts, SEPA payments, SWIFT transfers, business banking, global payments, currency exchange',
  ogImage = 'https://gcbusiness.space/Global_Chic_logo.jpeg',
  ogType = 'website',
  canonical,
  structuredData,
}) => {
  const fullTitle = `GCBusiness: ${title}`;
  const currentUrl = canonical || window.location.href;

  // Default structured data for business
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    "name": "GCBusiness",
    "description": "International banking and payment solutions for businesses",
    "url": "https://gcbusiness.space",
    "logo": "https://gcbusiness.space/Global_Chic_logo.jpeg",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+442046219434",
      "contactType": "customer service",
      "availableLanguage": "English"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "GB"
    },
    "serviceType": [
      "International Banking",
      "Multicurrency Accounts",
      "SEPA Payments",
      "SWIFT Transfers",
      "Currency Exchange"
    ]
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={currentUrl} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="GCBusiness" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@GlobalChicBiz" />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="GCBusiness" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </Helmet>
  );
};

